/**
 * Opening Hours Utility
 * Implements requirements 3.6, 9.1: Opening hours determination with detailed status
 * OSMのopening_hoursをopening_hours.jsで評価
 * 営業状況判定アルゴリズム（詳細な不明理由付き）
 */

import { OpenStatus } from '../types/core';

/**
 * 営業状況を判定する関数
 * @param spot - 判定対象のスポットデータ
 * @param apiSource - データ取得元の状況
 * @returns 詳細な営業状況
 */
export function determineOpenStatus(spot: any, apiSource: string = 'success'): OpenStatus {
    // API取得失敗時
    if (apiSource === 'api_error' || apiSource === 'failed') {
        return 'unknown_api_error';
    }

    // 営業時間情報が存在しない場合
    if (!spot.openingHours && !spot.businessHours && !spot.hours) {
        console.log('No opening hours data');
        return 'unknown_no_data';
    }

    // 営業時間情報はあるが解析失敗
    const openingHours = spot.openingHours || spot.businessHours || spot.hours;

    try {
        // opening_hours.js を使用した解析（実際の実装では外部ライブラリを使用）
        const status = parseOpeningHours(openingHours);
        console.log('Parsed opening hours status:', status, 'from:', openingHours);
        return status;
    } catch (error) {
        console.warn('Opening hours parsing failed:', error);
        return 'unknown_parse_failed';
    }
}

/**
 * 営業時間文字列を解析する関数
 * 実際の実装では opening_hours.js ライブラリを使用
 * @param hoursString - 営業時間文字列
 * @returns 営業状況
 */
function parseOpeningHours(hoursString: string): OpenStatus {
    if (!hoursString || typeof hoursString !== 'string') {
        return 'unknown_no_data';
    }

    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentDay = now.getDay(); // 0=日曜日, 1=月曜日, ...

    // 正規化
    const normalized = hoursString.toLowerCase().trim();

    try {
        // 24/7 パターン
        if (normalized.includes('24/7') || normalized.includes('24時間') || normalized.includes('always open')) {
            return 'open';
        }

        // 定休日パターン
        if (normalized.includes('closed') || normalized.includes('定休') || normalized.includes('休業')) {
            return 'closed';
        }

        // 現在の曜日を取得（0=日曜日, 1=月曜日, ...）
        const dayNames = ['日', '月', '火', '水', '木', '金', '土'];
        const currentDayName = dayNames[currentDay];

        console.log('Parsing hours for', currentDayName, ':', hoursString);

        // 今日の営業時間を抽出
        let todayHours = null;

        // 曜日別の営業時間パターンを検索
        const dayPatterns = [
            // "月: 08:00-18:00" 形式
            new RegExp(`${currentDayName}\\s*[：:]\\s*(\\d{1,2}):(\\d{2})\\s*[-–]\\s*(\\d{1,2}):(\\d{2})`),
            // "月 08:00-18:00" 形式
            new RegExp(`${currentDayName}\\s+(\\d{1,2}):(\\d{2})\\s*[-–]\\s*(\\d{1,2}):(\\d{2})`),
            // "月曜日: 08:00-18:00" 形式
            new RegExp(`${currentDayName}[曜日]*\\s*[：:]\\s*(\\d{1,2}):(\\d{2})\\s*[-–]\\s*(\\d{1,2}):(\\d{2})`)
        ];

        for (const pattern of dayPatterns) {
            const match = hoursString.match(pattern);
            if (match) {
                todayHours = {
                    startHour: parseInt(match[1]),
                    startMin: parseInt(match[2]),
                    endHour: parseInt(match[3]),
                    endMin: parseInt(match[4])
                };
                console.log('Found today hours from day pattern:', todayHours);
                break;
            }
        }

        // 今日の営業時間が見つからない場合、一般的なパターンを試行
        if (!todayHours) {
            const generalPatterns = [
                // HH:MM-HH:MM 形式
                /(\d{1,2}):(\d{2})\s*[-–~]\s*(\d{1,2}):(\d{2})/,
                // HH-HH 形式
                /(\d{1,2})\s*[-–~]\s*(\d{1,2})/,
                // HH:MM～HH:MM 形式
                /(\d{1,2}):(\d{2})\s*[～〜]\s*(\d{1,2}):(\d{2})/
            ];

            for (const pattern of generalPatterns) {
                const match = hoursString.match(pattern);
                if (match) {
                    if (match.length === 5) {
                        // HH:MM-HH:MM 形式
                        todayHours = {
                            startHour: parseInt(match[1]),
                            startMin: parseInt(match[2]),
                            endHour: parseInt(match[3]),
                            endMin: parseInt(match[4])
                        };
                    } else if (match.length === 3) {
                        // HH-HH 形式
                        todayHours = {
                            startHour: parseInt(match[1]),
                            startMin: 0,
                            endHour: parseInt(match[2]),
                            endMin: 0
                        };
                    }
                    console.log('Found general hours:', todayHours);
                    break;
                }
            }
        }

        // 営業時間が見つかった場合、現在時刻と比較
        if (todayHours) {
            const { startHour, startMin, endHour, endMin } = todayHours;

            // 時間の妥当性チェック
            if (startHour >= 0 && startHour <= 23 && endHour >= 0 && endHour <= 23 &&
                startMin >= 0 && startMin <= 59 && endMin >= 0 && endMin <= 59) {

                const startTime = startHour * 60 + startMin;
                const endTime = endHour * 60 + endMin;
                const currentTime = currentHour * 60 + currentMinute;

                console.log('Time comparison:', {
                    currentTime: `${currentHour}:${currentMinute.toString().padStart(2, '0')}`,
                    startTime: `${startHour}:${startMin.toString().padStart(2, '0')}`,
                    endTime: `${endHour}:${endMin.toString().padStart(2, '0')}`
                });

                // 営業時間内かチェック
                if (endTime > startTime) {
                    // 通常の営業時間（日をまたがない）
                    const isOpen = currentTime >= startTime && currentTime <= endTime;
                    console.log('Normal hours check:', isOpen);
                    return isOpen ? 'open' : 'closed';
                } else {
                    // 深夜営業（日をまたぐ）
                    const isOpen = currentTime >= startTime || currentTime <= endTime;
                    console.log('Overnight hours check:', isOpen);
                    return isOpen ? 'open' : 'closed';
                }
            }
        }

        // 営業時間が見つからない場合は解析失敗として返す
        console.log('No recognizable time pattern found');
        return 'unknown_parse_failed';

    } catch (error) {
        console.warn('Opening hours parsing error:', error);
        return 'unknown_parse_failed';
    }
}

/**
 * 営業状況の日本語表示テキストを取得
 * @param status - 営業状況
 * @returns 日本語表示テキスト
 */
export function getOpenStatusText(status: OpenStatus): string {
    const statusTexts = {
        open: '営業中',
        closed: '営業時間外',
        unknown_no_data: '不明（営業時間情報なし）',
        unknown_parse_failed: '不明（解析できず）',
        unknown_api_error: '不明（取得失敗）'
    };

    return statusTexts[status] || '不明';
}

/**
 * 営業状況の詳細説明を取得
 * @param status - 営業状況
 * @returns 詳細説明テキスト
 */
export function getOpenStatusDescription(status: OpenStatus): string {
    const descriptions = {
        open: '現在営業中です',
        closed: '現在は営業時間外です',
        unknown_no_data: '営業時間の情報が提供されていません',
        unknown_parse_failed: '営業時間の情報を正しく解析できませんでした',
        unknown_api_error: '営業時間の情報を取得できませんでした'
    };

    return descriptions[status] || '営業状況が不明です';
}

/**
 * 営業状況に基づくスコアを計算
 * @param status - 営業状況
 * @returns スコア（0-1）
 */
export function getOpenStatusScore(status: OpenStatus): number {
    const scores = {
        open: 1.0,           // 営業中は最高スコア
        closed: 0.0,         // 営業時間外は最低スコア
        unknown_no_data: 0.5,      // 不明は中間スコア
        unknown_parse_failed: 0.4, // 解析失敗は少し低め
        unknown_api_error: 0.3     // API失敗は更に低め
    };

    return scores[status] || 0.3;
}

/**
 * 営業時間文字列を正規化
 * @param hoursString - 元の営業時間文字列
 * @returns 正規化された営業時間文字列
 */
export function normalizeOpeningHours(hoursString: string): string {
    if (!hoursString) return '';

    return hoursString
        .replace(/\s+/g, ' ')           // 複数スペースを単一スペースに
        .replace(/[–—]/g, '-')          // 各種ダッシュをハイフンに統一
        .replace(/：/g, ':')            // 全角コロンを半角に
        .replace(/（/g, '(')            // 全角括弧を半角に
        .replace(/）/g, ')')
        .trim();
}

/**
 * 営業時間の妥当性をチェック
 * @param hoursString - チェック対象の営業時間文字列
 * @returns 妥当性チェック結果
 */
export function validateOpeningHours(hoursString: string): {
    isValid: boolean;
    errors: string[];
} {
    const errors: string[] = [];

    if (!hoursString || typeof hoursString !== 'string') {
        errors.push('営業時間文字列が空です');
        return { isValid: false, errors };
    }

    const normalized = normalizeOpeningHours(hoursString);

    // 基本的なパターンチェック
    const hasTimePattern = /\d{1,2}:?\d{0,2}\s*[-–]\s*\d{1,2}:?\d{0,2}/.test(normalized);
    const has24_7 = normalized.includes('24/7') || normalized.includes('24時間');

    if (!hasTimePattern && !has24_7) {
        errors.push('認識可能な時間パターンが見つかりません');
    }

    return {
        isValid: errors.length === 0,
        errors
    };
}

export default {
    determineOpenStatus,
    getOpenStatusText,
    getOpenStatusDescription,
    getOpenStatusScore,
    normalizeOpeningHours,
    validateOpeningHours
};